/**
 * The hierarchy: epics, features, stories and tasks as a tree with roll-up progress on every
 * parent. Ancestors of a filtered match stay (dimmed) so nothing loses its place. Keyboard:
 * arrows or j and k move, right and left open and close, Enter opens the item, x selects
 * (Shift x extends), Home and End.
 *
 * Row (40 px), the item identity line: chevron, kind (a checkbox takes its place on hover or
 * while anything is picked), key, title, then fixed columns: progress ("4 of 9") or points,
 * status, owner (hidden when every row has the same one), and the row's actions on hover:
 * Start mission and a menu. A mission waiting on a person replaces the status with the one
 * attention pill the row may carry ("Needs you", or "Blocked"), the same as the table and board.
 *
 * On a phone every row is two lines (one height for the list): the title, then the key and the
 * status as a caption, with an 8 px indent step and no action column so the title keeps its width
 * (it wraps to two lines rather than being cut; the drawer carries the row's actions).
 */
import { ArrowUpRight, ChevronRight, ListOrdered, MoreHorizontal, PanelRightOpen, Plus, Rocket, Trash2 } from '@orgus/ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { MISSION_KINDS } from '@orgus/protocol';
import type { BacklogItem, Id, QualityIssue } from '@orgus/protocol';
import {
  Avatar,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  REVEAL_GROUP,
  ROW_HEIGHT,
  RevealSlot,
  Tooltip,
  VIRTUALIZE_ABOVE,
  cn,
  useDensity,
  useVirtualRows,
} from '@orgus/ui';
import { SM_UP_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { headlineMission, isActive } from '../missions/model.ts';
import type { MissionRow } from '../missions/model.ts';
import { AttentionChip, attentionOf } from '../missions/MissionBlockerChip.tsx';
import { ItemState, KindIcon } from '../missions/parts.tsx';
import { buildTree, defaultExpanded, filterCount, ownerHint, ownersDiffer, treeKey, visibleRows } from './model.ts';
import type { ReqFilter } from './model.ts';
import { KeyLabel, Rollup } from './parts.tsx';

/** What the tree may do to a row beyond opening it. Each is optional: without it the action is not offered. */
export interface TreeActions {
  onStart?: (item: BacklogItem) => void;
  onStartSequence?: (item: BacklogItem) => void;
  onOpenMission?: (missionId: Id) => void;
  onDelete?: (item: BacklogItem) => void;
}

/** Bulk selection: which rows are picked, and how a row is picked (`range` with Shift). */
export interface TreeSelection {
  isSelected: (id: Id) => boolean;
  count: number;
  toggle: (id: Id, range?: boolean) => void;
}

/** A story, task, defect, spike or chore that is open and has no mission working on it. */
export function canStartMission(it: BacklogItem, rows: readonly MissionRow[]): boolean {
  if (!MISSION_KINDS.includes(it.kind) || it.status === 'done' || it.status === 'dropped') return false;
  const m = headlineMission(rows, it.id);
  return !m || !isActive(m.view.state);
}

export function TreeView({
  items,
  filter,
  rows,
  issues,
  selectedId,
  onOpen,
  onAddChild,
  projectName,
  showProject,
  actions = {},
  selection,
  onOrder,
}: {
  items: readonly BacklogItem[];
  filter: ReqFilter;
  rows: readonly MissionRow[];
  issues: ReadonlyMap<Id, QualityIssue[]>;
  selectedId: Id | null;
  onOpen: (id: Id) => void;
  onAddChild: (parent: BacklogItem) => void;
  projectName: (id: Id) => string;
  showProject: boolean;
  actions?: TreeActions;
  selection?: TreeSelection;
  /** The rows on screen, in order: Shift ranges and Select all reach only these. */
  onOrder?: (ids: Id[]) => void;
}) {
  const roots = useMemo(() => buildTree([...items], filter, issues), [items, filter, issues]);
  const narrowed = filterCount(filter) > 0;
  const [expanded, setExpanded] = useState<Set<Id>>(() => defaultExpanded(roots, narrowed));
  const [active, setActive] = useState<Id | null>(selectedId);
  const listRef = useRef<HTMLDivElement>(null);

  // A new filter opens everything it matched; clearing it goes back to epics and features.
  useEffect(() => setExpanded(defaultExpanded(roots, narrowed)), [narrowed, JSON.stringify(filter)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedId) setActive(selectedId);
  }, [selectedId]);

  const visible = useMemo(() => visibleRows(roots, expanded), [roots, expanded]);
  const order = useMemo(() => visible.map((r) => r.node.item.id), [visible]);
  useEffect(() => onOrder?.(order), [order, onOrder]);
  const showOwner = ownersDiffer(visible.map((r) => r.node.item.owner));
  const anyChecked = (selection?.count ?? 0) > 0;
  const toggle = (id: Id) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  // Only the rows on screen are in the DOM once the list is long: a 260-item backlog rendered
  // every row (about 1.5 MB of markup) on opening Missions and on every live update, and froze
  // the page for seconds (21 Oct 2026). Rows are one fixed height from sm up (the density's row
  // height, plus the gap before a block); below sm they wrap to two lines, so phones keep the
  // full list.
  const density = useDensity();
  // Server rendering (and tests) has no window: window the list there too.
  const wide = useMediaQuery(SM_UP_QUERY) || typeof window === 'undefined';
  const gapBefore = useCallback(
    (i: number) => {
      const r = visible[i];
      return r && r.node.depth === 0 && i > 0 && (r.hasChildren || visible[i - 1]!.node.depth > 0) ? BLOCK_GAP_PX : 0;
    },
    [visible],
  );
  const heightOf = useCallback((i: number) => ROW_HEIGHT[density] + gapBefore(i), [density, gapBefore]);
  const virt = useVirtualRows({ count: visible.length, heightOf, heightKey: `${density}:${visible.length}`, ref: listRef, enabled: wide && visible.length > VIRTUALIZE_ABOVE });
  const { scrollToIndex } = virt;

  useEffect(() => {
    if (!active) return;
    const el = listRef.current?.querySelector(`[data-tree-id="${CSS.escape(active)}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
    else scrollToIndex(order.indexOf(active));
  }, [active, order, scrollToIndex]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if ((e.target as HTMLElement).closest('[role="menu"], button[aria-haspopup]')) return;
    if (e.key === 'Enter' && active) {
      e.preventDefault();
      onOpen(active);
      return;
    }
    if ((e.key === 'x' || e.key === 'X') && active && selection) {
      e.preventDefault();
      selection.toggle(active, e.shiftKey);
      return;
    }
    if (e.key === ' ' && active) {
      e.preventDefault();
      toggle(active);
      return;
    }
    const r = treeKey(visible, active, e.key);
    if (r.active || r.toggle) e.preventDefault();
    if (r.toggle) toggle(r.toggle);
    if (r.active) setActive(r.active);
  };

  const all = items as BacklogItem[];
  return (
    <div
      ref={listRef}
      role="tree"
      aria-label="Backlog hierarchy"
      aria-multiselectable={selection ? true : undefined}
      tabIndex={0}
      aria-activedescendant={active ? `tree-${active}` : undefined}
      onKeyDown={onKeyDown}
      onFocus={() => !active && visible[0] && setActive(visible[0].node.item.id)}
      className="flex flex-col rounded-card border border-border bg-card p-2 surface-sheen shadow-elevation-flat focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      data-list
    >
      {virt.padTop > 0 && <div aria-hidden style={{ height: virt.padTop }} className="shrink-0" />}
      {visible.slice(virt.start, virt.end).map(({ node, expanded: open, hasChildren }, j) => {
        const i = virt.start + j;
        const it = node.item;
        const mission = headlineMission(rows, it.id);
        const running = mission && isActive(mission.view.state) ? mission : undefined;
        const attention = mission ? attentionOf(mission.view) : null;
        const isActiveRow = active === it.id;
        const checked = selection?.isSelected(it.id) ?? false;
        const parent = it.kind === 'epic' || it.kind === 'feature';
        const startable = !!actions.onStart && canStartMission(it, rows);
        const canAdd = it.kind === 'epic' || it.kind === 'feature' || it.kind === 'story';
        const onRowClick = (e: MouseEvent) => {
          setActive(it.id);
          // Shift or Ctrl picks rows instead of opening one.
          if (selection && (e.shiftKey || e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            selection.toggle(it.id, e.shiftKey);
            return;
          }
          onOpen(it.id);
        };
        return (
          <div
            key={it.id}
            id={`tree-${it.id}`}
            role="treeitem"
            aria-level={node.depth + 1}
            aria-expanded={hasChildren ? open : undefined}
            aria-selected={selection ? checked : selectedId === it.id}
            aria-current={selectedId === it.id || undefined}
            data-tree-id={it.id}
            data-row
            onClick={onRowClick}
            className={cn(
              REVEAL_GROUP,
              'group/trow relative flex min-h-row-2line shrink-0 cursor-pointer select-none items-center gap-2 rounded-control py-2 pl-1 pr-2 transition-colors duration-(--duration-fast) sm:h-row sm:min-h-0 sm:gap-3 sm:py-0 sm:pl-2',
              // A block (an epic with its children) has space around it, never a rule; loose
              // top-level items after it run at the list's one row rhythm.
              node.depth === 0 && i > 0 && (hasChildren || visible[i - 1]!.node.depth > 0) && 'mt-3',
              selectedId === it.id || checked ? 'selected-tint' : isActiveRow ? 'bg-active' : 'hover:bg-hover',
              !node.matched && 'opacity-60',
            )}
          >
            {/*
              One indent step per level (24 px, the group step; 12 px on a phone): the 24 px fold
              button, then the kind icon 4 px after it, so a child's chevron sits under its parent's icon.
            */}
            <span className="flex shrink-0 items-center">
              {Array.from({ length: node.depth }, (_, k) => (
                <span key={k} className="w-2 shrink-0 sm:w-3 md:w-6" aria-hidden />
              ))}
              {hasChildren ? (
                <IconButton
                  size="xs"
                  noTooltip
                  tabIndex={-1}
                  label={open ? `Close ${it.title}` : `Open ${it.title}`}
                  icon={<ChevronRight className={cn('transition-transform duration-(--duration-fast)', open && 'rotate-90')} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(it.id);
                  }}
                />
              ) : (
                <span className="h-6 w-4 shrink-0 sm:w-6" aria-hidden />
              )}
              {/* The checkbox takes the kind icon's place while it shows, so the two never draw over each other. */}
              <span className="relative ml-1 flex size-4 shrink-0 items-center justify-center">
                <KindIcon
                  kind={it.kind}
                  className={cn(
                    'transition-opacity duration-(--duration-fast)',
                    selection && (anyChecked ? 'opacity-0' : 'pointer-fine:group-hover/trow:opacity-0 pointer-fine:group-focus-within/trow:opacity-0'),
                  )}
                />
                {selection && (
                  <span
                    className={cn(
                      'absolute inset-0 flex items-center justify-center transition-opacity duration-(--duration-fast)',
                      !anyChecked && 'opacity-0 pointer-fine:group-hover/trow:opacity-100 pointer-fine:group-focus-within/trow:opacity-100',
                    )}
                  >
                    <Checkbox
                      aria-label={`Select ${it.key} ${it.title}`}
                      checked={checked}
                      tabIndex={-1}
                      onClick={(e) => e.stopPropagation()}
                      onCheckedChange={(_, e) => selection.toggle(it.id, (e.nativeEvent as globalThis.MouseEvent).shiftKey)}
                    />
                  </span>
                )}
              </span>
            </span>
            <KeyLabel value={it.key} className="hidden w-20 sm:inline" />
            <span className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline sm:gap-2">
              <span className={cn('line-clamp-2 text-ui text-fg wrap-anywhere sm:line-clamp-none sm:truncate', parent && 'font-medium')} title={showOwner ? undefined : ownerHint(it.owner)}>
                {it.title}
              </span>
              {showProject && node.depth === 0 && (
                <span className="hidden shrink-0 truncate text-caption text-fg-subtle lg:inline" data-meta>
                  {projectName(it.projectId)}
                </span>
              )}
              {/* Phone: the status first, icon plus label; the key is a reference, so it trails, quiet. */}
              <span className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle sm:hidden" data-meta>
                {attention ? <AttentionChip view={mission!.view} short /> : <ItemState status={it.status} kind={it.kind} className="min-w-0 gap-1 text-caption" />}
                <KeyLabel value={it.key} />
              </span>
            </span>
            <span className="hidden w-28 shrink-0 justify-end md:flex" data-column="progress">
              {hasChildren ? <Rollup item={it} items={all} /> : it.estimate != null ? <span className="text-caption text-fg-muted num">{it.estimate} pts</span> : null}
            </span>
            <span className="hidden w-32 shrink-0 sm:flex" data-column="status">
              {attention ? <AttentionChip view={mission!.view} short /> : <ItemState status={it.status} kind={it.kind} />}
            </span>
            {showOwner && (
              <span className="hidden w-6 shrink-0 justify-center lg:flex" data-column="owner">
                {it.owner && (
                  <Tooltip content={`Owner: ${it.owner}`}>
                    <span tabIndex={-1} className="inline-flex">
                      <Avatar name={it.owner} size={20} />
                    </span>
                  </Tooltip>
                )}
              </span>
            )}
            {/* Actions on hover or focus, in their own column so they never draw over the row's text. */}
            <RevealSlot className="hidden w-14 shrink-0 items-center justify-end gap-1 sm:flex" onClick={(e) => e.stopPropagation()}>
              {startable ? (
                <IconButton className="hidden sm:inline-flex" size="xs" label={`Start a mission on ${it.key}`} icon={<Rocket />} onClick={() => actions.onStart!(it)} />
              ) : running && actions.onOpenMission ? (
                <IconButton className="hidden sm:inline-flex" size="xs" label={`Open the mission on ${it.key}`} icon={<ArrowUpRight />} onClick={() => actions.onOpenMission!(running.mission.id)} />
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton size="xs" label={`More actions for ${it.key}`} icon={<MoreHorizontal />} noTooltip />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem icon={<PanelRightOpen />} onSelect={() => onOpen(it.id)}>
                    Open {it.key}
                  </DropdownMenuItem>
                  {startable && (
                    <DropdownMenuItem icon={<Rocket />} onSelect={() => actions.onStart!(it)}>
                      Start mission…
                    </DropdownMenuItem>
                  )}
                  {running && actions.onOpenMission && (
                    <DropdownMenuItem icon={<ArrowUpRight />} onSelect={() => actions.onOpenMission!(running.mission.id)}>
                      Open mission
                    </DropdownMenuItem>
                  )}
                  {hasChildren && actions.onStartSequence && it.status !== 'done' && it.status !== 'dropped' && (
                    <DropdownMenuItem icon={<ListOrdered />} onSelect={() => actions.onStartSequence!(it)}>
                      Run its items in order…
                    </DropdownMenuItem>
                  )}
                  {canAdd && (
                    <DropdownMenuItem icon={<Plus />} onSelect={() => onAddChild(it)}>
                      Add item under it…
                    </DropdownMenuItem>
                  )}
                  {actions.onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem icon={<Trash2 />} danger onSelect={() => actions.onDelete!(it)}>
                        Delete {it.key}…
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </RevealSlot>
          </div>
        );
      })}
      {virt.padBottom > 0 && <div aria-hidden style={{ height: virt.padBottom }} className="shrink-0" />}
    </div>
  );
}

/** The space before a block (an epic with its children): Tailwind mt-3. */
const BLOCK_GAP_PX = 12;
